/**
 * Bots — every grid bot on the account, with its id, state and P&L.
 * Start a new one, stop or resume an existing one, open its Details.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Card } from '../../components/UI';
import { MarketClock } from '../../components/MarketClock';
import { useBreakpoint } from '../../lib/useBreakpoint';
import {
  deleteBot,
  getBotDefaults,
  getConnection,
  isWorking,
  listBots,
  resumeBot,
  stopBot,
  type BotDefaults,
  type BotSummary,
  type AccountTax,
  type ConnectionView,
} from '../../lib/bots';
import { useConfirm } from '../../components/Confirm';
import { ConnectionPanel } from './ConnectionPanel';
import { NewBotForm } from './NewBotForm';
import {
  Caption,
  BotsStyles,
  CardHead,
  Empty,
  EnvPill,
  ErrorLine,
  FlashNumber,
  HealthPill,
  Kpi,
  Notice,
  StatePill,
  TableWrap,
  Td,
  Th,
  fmt,
  fmtInt,
  mono,
  pageStyle,
  pnlColor,
  signed,
  usePoll,
} from './ui';

interface Props {
  onOpen: (id: string) => void;
}

export const Bots = ({ onOpen }: Props) => {
  const isMobile = useBreakpoint() === 'mobile';
  const [bots, setBots] = useState<BotSummary[] | null>(null);
  const [connection, setConnection] = useState<ConnectionView | null>(null);
  const [defaults, setDefaults] = useState<BotDefaults | null>(null);
  const [tax, setTax] = useState<AccountTax | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  /**
   * Working bots and finished ones answer different questions. The first is
   * "what is my money doing right now", checked many times a day; the second
   * is "what did that bot end up doing", read occasionally. Mixing them pushed
   * the live rows below the fold as soon as a few bots were stopped.
   */
  const [tab, setTab] = useState<'active' | 'stopped'>('active');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    try {
      const [list, conn] = await Promise.all([listBots(), getConnection()]);
      setBots(list.bots);
      setTax(list.tax);
      setConnection(conn);
      setError(null);
      if (!defaults) setDefaults(await getBotDefaults());
    } catch (e) {
      const err = e as { status?: number; message: string };
      if (err.status === 403 || err.status === 404) setForbidden(true);
      setError(err.message);
    }
  };
  usePoll(load, 4000);

  const act = async (bot: BotSummary, fn: () => Promise<unknown>) => {
    setBusyId(bot.id);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(`${bot.label} ${bot.symbol}: ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  };

  if (forbidden) {
    return (
      <div style={pageStyle(isMobile)}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>Bots</h1>
        <Notice tone="down">{error ?? 'This account is not allowed to use bots.'}</Notice>
      </div>
    );
  }

  const status = connection?.status ?? null;
  const ready = status?.state === 'ready' && !status.unavailable;
  const all = bots ?? [];
  const working = all.filter((b) => !b.archivedAt && isWorking(b.state));
  // Archived bots are stopped bots with the record kept, so they belong here too.
  const stopped = all.filter((b) => b.archivedAt || !isWorking(b.state));
  const visibleBots =
    tab === 'active' ? working : stopped.filter((b) => showArchived || !b.archivedAt);
  const accountingBots = (bots ?? []).filter((b) => b.env === (status?.env ?? 'live'));
  const totals = accountingBots.reduce(
    (t, b) => ({
      // Everything on the broker's basis, so these foot to the EOD confirmation.
      gross: t.gross + b.account.realised,
      // Realised, split by what earned it. Grid is the repeatable strategy
      // income; trend is directional and is not evidence the grid works.
      grid: t.grid + b.account.gridRealised,
      trend: t.trend + b.account.trendRealised,
      gridCycles: t.gridCycles + b.account.gridCycles,
      trendCycles: t.trendCycles + b.account.trendCycles,
      estCost: t.estCost + b.account.chargesInCost + b.account.chargesRealised,
      unrealised: t.unrealised + (b.account.unrealised ?? 0),
      exposure: t.exposure + b.account.costBasis,
      orders: t.orders + (b.tradingDay === status?.budget?.day ? b.ordersToday : 0),
    }),
    {
      gross: 0,
      grid: 0,
      trend: 0,
      gridCycles: 0,
      trendCycles: 0,
      estCost: 0,
      unrealised: 0,
      exposure: 0,
      orders: 0,
    },
  );
  const cycles = totals.gridCycles + totals.trendCycles;
  const netPerCycle = cycles > 0 ? (totals.grid + totals.trend) / cycles : null;

  return (
    <div style={{ ...pageStyle(isMobile), gap: 20 }}>
      <BotsStyles />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: '-0.03em' }}>Bots</h1>
        <MarketClock />
        <div style={{ flex: 1 }} />
        {tab === 'stopped' && (bots ?? []).some((b) => b.archivedAt) && (
          <Button variant="ghost" onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? 'Hide archived' : 'Show archived'}
          </Button>
        )}
        {!creating && (
          <Button icon="plus" disabled={!ready || !defaults} onClick={() => setCreating(true)}>
            New bot
          </Button>
        )}
      </div>

      <ConnectionPanel
        view={connection}
        onChange={setConnection}
        hasWorkingBots={working.length > 0}
      />

      {accountingBots.some((b) => b.account.issues.length > 0) && (
        <Notice tone="warn">
          Accounting estimate:{' '}
          {[...new Set(accountingBots.flatMap((b) => b.account.issues))].join('; ')}.
        </Notice>
      )}

      {status?.state === 'ready' && status.unavailable && (
        <Notice tone="warn">Bots cannot start: {status.unavailable}.</Notice>
      )}

      {creating && defaults && status?.env && (
        <NewBotForm
          env={status.env}
          defaults={defaults}
          onCancel={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onOpen(id);
          }}
        />
      )}

      {bots && bots.length > 0 && (
        <div
          style={{
            display: 'grid',
            /**
             * Attribution first and widest, then the three account-level
             * figures. Grid, trend and inventory belong together because they
             * are the same money asked three different questions; Total,
             * Charges and CGT are each about the account as a whole.
             */
            gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.9fr) repeat(3, minmax(0, 1fr))',
            gap: 16,
            alignItems: 'stretch',
          }}
        >
          <Tile>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 16,
                height: '100%',
              }}
            >
              <Attribution
                label="Grid"
                hint={`Quantity bought AND sold the same day, net of charges — the repeatable market-making income. ${fmtInt(totals.gridCycles)} closed ${totals.gridCycles === 1 ? 'cycle' : 'cycles'}. Same-day quantity pays brokerage once, which is why it clears a 0.5% spread where carried stock struggles to.`}
                value={totals.grid}
                sub={`${fmtInt(totals.gridCycles)} ${totals.gridCycles === 1 ? 'cycle' : 'cycles'}`}
              />
              <Attribution
                label="Trend"
                hint={`Sales out of stock carried from a prior day, net of charges. Directional — the market moving under inventory the bots happened to hold, which is not evidence the grid works. ${fmtInt(totals.trendCycles)} closed.`}
                value={totals.trend}
                sub={`${fmtInt(totals.trendCycles)} ${totals.trendCycles === 1 ? 'cycle' : 'cycles'}`}
              />
              <Attribution
                label="Inventory"
                hint="Unrealised mark-to-market on what is held now: market value less what the shares cost to open, with entry charges inside that cost — so a gain appears only once the position has covered what it cost to open. It does NOT net the cost of selling out, which for a full exit is roughly 0.18% of the position."
                value={totals.unrealised}
                sub="net of entry fees"
              />
            </div>
          </Tile>

          <Tile>
            <Kpi
              label="Total"
              hint="Grid plus trend plus inventory. Realised and unrealised are each already net of charges, so this is their sum — the charges beside it are shown for visibility, not to be taken off again."
              size={26}
              color={pnlColor(totals.grid + totals.trend + totals.unrealised)}
              value={
                <FlashNumber
                  value={totals.grid + totals.trend + totals.unrealised}
                  text={signed(totals.grid + totals.trend + totals.unrealised)}
                />
              }
            >
              <span style={{ fontSize: 11, color: 'var(--tk-ink-3)', ...mono }}>
                {netPerCycle === null
                  ? 'no closed cycles yet'
                  : `${signed(netPerCycle)} per cycle · ${fmtInt(cycles)} closed`}
              </span>
            </Kpi>
          </Tile>

          <Tile>
            <Kpi
              label="All charges"
              hint="Brokerage, its sales tax, the levies and CDC — across every bot, already inside the figures beside this. Charged once on quantity bought and sold the same day, which is why a carried round trip costs about twice a same-day one."
              size={26}
              color={pnlColor(-totals.estCost)}
              value={<FlashNumber value={-totals.estCost} text={signed(-totals.estCost)} />}
            />
          </Tile>

          <Tile>
            <Kpi
              label={`Live est. CGT ${tax ? tax.month : ''}`}
              hint={
                tax?.issues.length
                  ? tax.issues.join('; ')
                  : tax
                    ? `NCCPL basis: proceeds less 0.5%, cost plus 0.5%, estimated from ${tax.cycles} ${tax.cycles === 1 ? 'disposal' : 'disposals'} this month, with fiscal-year loss offsets. Net taxable gain ${signed(tax.taxableGain)}. An estimate for recorded live accounts only — NCCPL nets across every broker under your UIN.`
                    : 'Estimated capital gains tax for the account this month'
              }
              size={26}
              color={tax?.cgt != null && tax.cgt > 0 ? 'var(--tk-down)' : 'var(--tk-ink-2)'}
              value={tax?.cgt != null ? signed(-tax.cgt) : '—'}
            />
          </Tile>
        </div>
      )}

      <Card padding={24}>
        <CardHead
          title={tab === 'active' ? 'Working bots' : 'Stopped bots'}
          right={bots ? `${visibleBots.length} shown` : undefined}
        />
        <div role="tablist" aria-label="Bots" style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
          {(
            [
              ['active', 'Working', working.length],
              ['stopped', 'Stopped', stopped.length],
            ] as const
          ).map(([key, label, n]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                color: tab === key ? 'var(--tk-ink)' : 'var(--tk-ink-2)',
                background: tab === key ? 'var(--tk-surface-2)' : 'transparent',
              }}
            >
              {label} <span style={{ opacity: 0.6, fontWeight: 500 }}>{n}</span>
            </button>
          ))}
        </div>
        <ErrorLine>{error}</ErrorLine>
        {!bots ? (
          <Empty>Loading…</Empty>
        ) : visibleBots.length === 0 ? (
          <Empty>
            {tab === 'stopped'
              ? 'No stopped bots.'
              : ready
                ? 'No bots working.'
                : 'Connect the broker to start a bot.'}
          </Empty>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th left>Bot</Th>
                <Th left>Symbol</Th>
                <Th left>State</Th>
                <Th>Holding</Th>
                <Th>Grid</Th>
                <Th>Trend</Th>
                <Th>Inventory</Th>
                <Th>Cycles</Th>
                <Th>Total</Th>
                <Th>Orders</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {visibleBots.map((b) => (
                <tr key={b.id} className="tkb-row tkb-click" onClick={() => onOpen(b.id)}>
                  <Td left bold title={b.id}>
                    <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                      {b.label} <EnvPill env={b.env} />
                    </span>
                  </Td>
                  <Td left bold>
                    {/* The grid's shape belongs to the symbol, not to its own
                        column — and it freed the word "Grid" for the P&L. */}
                    <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                      {b.symbol}
                      <span style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--tk-ink-3)' }}>
                        {fmtInt(b.params.lotSize)} × ±{fmt(b.params.spreadPct)}%
                      </span>
                    </span>
                  </Td>
                  <Td left title={b.error ?? b.waitingFor ?? undefined}>
                    <span
                      style={{
                        display: 'inline-flex',
                        gap: 8,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <StatePill state={b.state} />
                      <HealthPill health={b.health} />
                      {b.state === 'STARTING' && b.waitingFor ? (
                        <span
                          style={{ color: 'var(--tk-ink-3)', fontSize: 11.5, whiteSpace: 'normal' }}
                        >
                          {b.waitingFor}
                        </span>
                      ) : null}
                    </span>
                  </Td>
                  <Td>
                    {fmtInt(b.account.quantity)}{' '}
                    <span
                      style={{ color: 'var(--tk-ink-3)' }}
                      title={`Weighted average with charges in it — the broker's avg rate. Exposure ${fmt(b.account.costBasis)}; charges ${fmt(b.account.chargesInCost + b.account.chargesRealised)} of it.`}
                    >
                      @ {fmt(b.account.avgCost)}
                    </span>
                  </Td>
                  <Td
                    color={pnlColor(b.account.gridRealised)}
                    title="Same-day round trips, net of charges — the repeatable part of what this bot earns"
                  >
                    {signed(b.account.gridRealised)}
                  </Td>
                  <Td
                    color={pnlColor(b.account.trendRealised)}
                    title="Sales out of stock carried from a prior day — directional, and not evidence the grid works"
                  >
                    {signed(b.account.trendRealised)}
                  </Td>
                  <Td
                    color={pnlColor(b.account.unrealised)}
                    title="Unrealised mark-to-market on what is held now, gross of the cost of selling out"
                  >
                    {signed(b.account.unrealised)}
                  </Td>
                  <Td
                    color="var(--tk-ink-3)"
                    title={`${fmtInt(b.account.gridCycles)} same-day, ${fmtInt(b.account.trendCycles)} delivery${b.account.netPerCycle === null ? '' : ` · ${signed(b.account.netPerCycle)} net per cycle`}`}
                  >
                    {fmtInt(b.account.gridCycles)}
                    <span style={{ color: 'var(--tk-ink-3)' }}>
                      {' '}
                      / {fmtInt(b.account.trendCycles)}
                    </span>
                  </Td>
                  <Td
                    color={pnlColor(b.account.total)}
                    bold
                    title="Realised plus unrealised — what this bot is worth having done, however the two are split"
                  >
                    {signed(b.account.total)}
                  </Td>
                  <Td>
                    {fmtInt(b.ordersToday)}
                    {b.params.maxOrders ? (
                      <span style={{ color: 'var(--tk-ink-3)' }}>
                        {' '}
                        / {fmtInt(b.params.maxOrders)}
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    <span
                      style={{ display: 'inline-flex', gap: 6 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RowActions
                        bot={b}
                        busy={busyId === b.id}
                        canResume={ready && status?.env === b.env}
                        act={act}
                      />
                      <Button size="sm" variant="subtle" onClick={() => onOpen(b.id)}>
                        Details
                      </Button>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </div>
  );
};

/** The card every figure on the summary row sits in. */
const Tile = ({ children }: { children: ReactNode }) => (
  <div
    className="tkb-tile"
    style={{
      background: 'var(--tk-card-bg, var(--tk-surface))',
      border: 'var(--tk-card-border, 1px solid var(--tk-line))',
      borderRadius: 'var(--tk-radius-lg)',
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minWidth: 0,
    }}
  >
    {children}
  </div>
);

/**
 * One of the three attribution figures, set a size below Total.
 *
 * They share a box because they are the same money answered three ways —
 * what the strategy earned, what the market gave, and what is still at risk —
 * and reading them apart from each other is how a directional gain gets
 * mistaken for a working algorithm.
 */
const Attribution = ({
  label,
  hint,
  value,
  sub,
}: {
  label: string;
  hint: string;
  value: number;
  sub: string;
}) => (
  <div title={hint} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
    <Caption>{label}</Caption>
    <span
      style={{
        fontSize: 19,
        fontWeight: 600,
        letterSpacing: '-0.02em',
        color: pnlColor(value),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...mono,
      }}
    >
      <FlashNumber value={value} text={signed(value)} />
    </span>
    <span style={{ fontSize: 10.5, color: 'var(--tk-ink-3)', ...mono }}>{sub}</span>
  </div>
);

export const RowActions = ({
  bot,
  busy,
  canResume,
  act,
}: {
  bot: BotSummary;
  busy: boolean;
  canResume: boolean;
  act: (bot: BotSummary, fn: () => Promise<unknown>) => Promise<void>;
}) => {
  const { ask, dialog } = useConfirm();
  const name = `${bot.label} ${bot.symbol}`;
  if (bot.archivedAt) return <span>Archived</span>;

  if (isWorking(bot.state)) {
    /**
     * Stop asks first, like Start and Delete already do.
     *
     * It sits one row away from Details and was the only destructive action on
     * the page that went straight through: a misclick on 22 Sep 2026 cancelled
     * PSO's pair mid-session. The prompt says what actually happens — the
     * orders go, the shares stay, and starting again re-centres the grid, so it
     * is not an undo — because "are you sure?" tells someone nothing they did
     * not already know.
     */
    const stopping = bot.state === 'STOPPING';
    return (
      <>
        <Button
          size="sm"
          variant="down"
          disabled={busy}
          onClick={() =>
            ask(
              stopping
                ? {
                    title: `Send ${name}'s cancels again?`,
                    body: (
                      <p style={{ margin: 0 }}>
                        It is already stopping, so a cancel has gone out and has not come back.
                        Sending another costs one order from today’s allowance.
                      </p>
                    ),
                    confirmLabel: 'Send again',
                  }
                : {
                    title: `Stop ${name}?`,
                    body: (
                      <>
                        <p style={{ margin: 0 }}>
                          Its resting orders are cancelled. The{' '}
                          <strong style={{ ...mono, color: 'var(--tk-ink)' }}>
                            {fmtInt(bot.account.quantity)}
                          </strong>{' '}
                          shares it holds stay in the account.
                        </p>
                        <p style={{ margin: 0 }}>
                          Starting it again re-centres the grid on the market at that moment, so
                          this is not an undo.
                        </p>
                      </>
                    ),
                    confirmLabel: 'Stop the bot',
                  },
              () => void act(bot, () => stopBot(bot.id)),
            )
          }
        >
          {stopping ? 'Stop again' : 'Stop'}
        </Button>
        {dialog}
      </>
    );
  }
  return (
    <>
      {/* A cash-starved bot stopped POSTING but cancelled nothing, so its book
          may still be at the exchange, and Start refuses while anything is
          open. Stop is how it is cleared; on an empty book it is harmless. */}
      {bot.state === 'CASH_STARVED' && (
        <Button
          size="sm"
          variant="down"
          disabled={busy}
          onClick={() =>
            ask(
              {
                title: `Cancel what ${name} left resting?`,
                body: (
                  <p style={{ margin: 0 }}>
                    It ran out of cash and stopped posting, but did not cancel its resting orders.
                    Stop cancels them so it can be started again; the shares stay in the account.
                  </p>
                ),
                confirmLabel: 'Stop the bot',
              },
              () => void act(bot, () => stopBot(bot.id)),
            )
          }
        >
          Stop
        </Button>
      )}
      <Button
        size="sm"
        variant="up"
        disabled={busy || !canResume}
        onClick={() => {
          const live = bot.env === 'live';
          if (!live) return void act(bot, () => resumeBot(bot.id, false));
          ask(
            {
              title: `Start ${name} with real money?`,
              body: (
                <>
                  <p style={{ margin: 0 }}>
                    This is a LIVE bot. It places real orders against the account as soon as the
                    market gives it a price to work from.
                  </p>
                  <p style={{ margin: 0 }}>
                    It re-centres on the market at that moment, not on where it left off.
                  </p>
                </>
              ),
              confirmLabel: 'Start trading',
              tone: 'up',
            },
            () => void act(bot, () => resumeBot(bot.id, true)),
          );
        }}
      >
        Start
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={busy}
        onClick={() =>
          ask(
            {
              title: `Archive ${name}?`,
              body: (
                <>
                  <p style={{ margin: 0 }}>
                    Its execution history and P&L remain in accounting. The bot is removed from the
                    active list.
                  </p>
                  <p style={{ margin: 0 }}>
                    The shares stay in the account — archiving the bot does not sell anything.
                  </p>
                </>
              ),
              confirmLabel: 'Archive bot',
            },
            () => void act(bot, () => deleteBot(bot.id)),
          )
        }
      >
        Archive
      </Button>
      {dialog}
    </>
  );
};
